import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { IconButton } from './IconButton';
import { useRestoreFocus } from './useRestoreFocus';

const SIZES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' } as const;

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Elemento que abre el diálogo (se renderiza como disparador; recupera el foco al cerrar). */
  trigger?: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
  /** Botones de acción: en móvil se apilan (el primario arriba), en escritorio van a la derecha. */
  footer?: ReactNode;
  size?: keyof typeof SIZES;
  hideClose?: boolean;
  /** Distintivo sobre el título (p. ej. el icono de un aviso). Decorativo. */
  icon?: ReactNode;
  /** `alertdialog` para avisos que exigen respuesta; el foco cae en el primer botón. */
  role?: 'dialog' | 'alertdialog';
  className?: string;
}

/** Diálogo modal (Radix): foco atrapado, Esc y clic fuera cierran, el foco vuelve a quien lo abrió. */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose = false,
  icon,
  role = 'dialog',
  className,
}: DialogProps) {
  const { t } = useT();
  const restoreFocus = useRestoreFocus(open);
  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="anim-overlay fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-board/60 p-4">
          <DialogPrimitive.Content
            onCloseAutoFocus={restoreFocus}
            role={role}
            {...(description ? {} : { 'aria-describedby': undefined })}
            className={cn(
              'anim-dialog surface-float relative w-full rounded-lg p-6',
              SIZES[size],
              className,
            )}
          >
            {icon ? <div className="mb-3">{icon}</div> : null}
            <DialogPrimitive.Title className="pr-10 font-display text-xl font-bold text-fg">
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-sm text-fg-muted">
                {description}
              </DialogPrimitive.Description>
            ) : null}
            {children ? <div className="mt-4">{children}</div> : null}
            {footer ? (
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {footer}
              </div>
            ) : null}
            {hideClose ? null : (
              <DialogPrimitive.Close asChild>
                <IconButton
                  label={t('actions.close')}
                  icon={<Icon icon={X} />}
                  iconSize="sm"
                  className="absolute right-3 top-3"
                />
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
